<template>
  <div class="risk-category">
    <div v-for="(category, index) in riskCategories" :key="index" class="category-div">
      <div v-if="categorySectionDisplayed(category.name)">
        <v-row no-gutters>
          <v-col class="d-flex align-center">
            <v-icon
              :id="getToggleCategoryIconId(category.name)"
              class="icon"
              @click.stop="toggleCategory(index)"
            >
              {{ !category.displayed ? 'mdi-chevron-right' : 'mdi-chevron-down' }}
            </v-icon>
            <div
              :id="getToggleCategoryId(category.name)"
              class="name"
              @click.stop="toggleCategory(index)"
            >
              {{ category.name }}
            </div>
            <div v-if="0 > excludedRiskCategories.indexOf(category.name)" class="quantity ml-3">
              {{ typesCount(category.name) }} Types
            </div>
            <div v-if="'Activities' === category.name" class="quantity ml-3">
              {{ activityTypesCount }} Types
            </div>
            <div v-if="highRiskCategoryName !== category.name" class="quantity ml-3">
              {{ approvedProvidersCount(category.name) }} Providers
            </div>
          </v-col>
          <v-col class="d-flex justify-end">
            <complete-checklist
              v-if="'Activities' !== category.name"
              :category-name="category.name"
              :checklist-view-mode-enabled="false"
              :form-control-enabled="formControlEnabled"
              :associated-checklist-completed="getTicked(category)"
              :element-disabled="
                (0 >= typesCount(category.name) && category.name !== excludedRiskCategories[1]) ||
                (0 >= approvedProvidersCount(category.name) &&
                  category.name === excludedRiskCategories[1]) ||
                (checklistFetchActionInProgress &&
                  checklistFetchActionCategoryName === category.name)
              "
              :checklist-fetch-action-category-name="checklistFetchActionCategoryName"
              :checklist-fetch-action-in-progress="checklistFetchActionInProgress"
              :save-draft="saveDraft"
            />
            <view-policy
              v-if="
                (excursionPolicyViewActionEnabled && 'Activities' !== category.name) ||
                'Outdoor Education Providers' === category.name
              "
              :category-name="category.name"
              :element-disabled="
                (0 >= typesCount(category.name) && category.name !== excludedRiskCategories[1]) ||
                (0 >= approvedProvidersCount(category.name) &&
                  category.name === excludedRiskCategories[1])
              "
            />
            <view-policy
              v-if="excursionPolicyViewActionEnabled && 'Activities' === category.name"
              :category-name="category.name"
              :element-disabled="0 >= activityTypesCount"
              :activity-supervisor-scroll-displayed="false"
            />
          </v-col>
        </v-row>
        <v-row v-if="category.displayed" dense>
          <v-col cols="12" class="pb-4">
            <select-control
              v-if="
                0 > excludedRiskCategories.indexOf(category.name) &&
                category.name !== highRiskCategoryName
              "
              :id="getTypeControlId(category.name)"
              background-color="#EEEEEE"
              :name="category.name"
              :form-control-enabled="formControlEnabled"
              @change-risk-category-done="changeRiskCategory"
            ></select-control>
            <high-risk
              v-if="category.name === highRiskCategoryName"
              :id="getTypeControlId(category.name)"
              background-color="#EEEEEE"
              :name="category.name"
              :form-control-enabled="formControlEnabled"
              @change-risk-category-done="changeRiskCategory"
            ></high-risk>
            <activity-risk-category
              v-if="'Activities' === category.name"
              :form-control-enabled="formControlEnabled"
              :checklist-and-policy-displayed="true"
              :save-draft="saveDraft"
              @change-activity-done="changeActivityDone"
            ></activity-risk-category>
          </v-col>
          <v-col
            v-if="0 < getProvidersByCategoryCount(category.name)"
            class="sub-category-container"
            cols="12"
          >
            Providers
            <stb-icon-tooltip
              v-if="hasNegativeProviderAnswer(category.name)"
              clickable
              icon-color="rgb(119, 119, 119)"
            >
              <span class="provider-tooltip-msg" :iconId="getProviderTooltipBtnId(category.name)">
                {{ negativeProviderAnswerHint }}
              </span>
            </stb-icon-tooltip>
            <provider-select
              :category-name="category.name"
              :form-control-enabled="formControlEnabled"
            ></provider-select>
          </v-col>
          <v-col
            v-if="excursionPersonResponsibleEnabled"
            :id="`risk-category-txt-people-responsible-${getTypeControlId(category.name)}`"
            cols="12"
          >
            <span class="text-field-title">People or Providers Responsible</span>
            <v-textarea
              :id="`personResponsible-${getTypeControlId(category.name)}`"
              ref="items"
              variant="solo"
              :model-value="category.personResponsible"
              :no-resize="true"
              :placeholder="personResponsiblePlaceholder"
              class="excursion-people-responsible-textarea"
              :disabled="
                !formControlEnabled ||
                (0 >= approvedProvidersCount(category.name) &&
                  'Outdoor Education Providers' === category.name)
              "
              @update:model-value="updatePersonResponsible(category, $event)"
            />
            <div
              v-if="checkIfAnyElementVerticalScrollEnabled(category.name)"
              :id="`risk-category--person-responsible-show-more--button-${getTypeControlId(
                category.name
              )}`"
              class="show-more-link"
              @click="
                displayDetailsDialog('People or Providers Responsible', category.personResponsible)
              "
            >
              Show more
            </div>
          </v-col>
          <v-col v-if="additionalRiskControlMitigationStrategiesEnabled" class="text-field-title">
            <span>Additional Risk Control Mitigation Strategies</span>
            <v-textarea
              :id="`additionalRiskControl-${getTypeControlId(category.name)}`"
              ref="items"
              variant="solo"
              :model-value="category.additionalRiskControlMitigationStrategies"
              :no-resize="true"
              :placeholder="additionalRiskControlPlaceholder"
              :disabled="
                !formControlEnabled ||
                (0 >= approvedProvidersCount(category.name) &&
                  'Outdoor Education Providers' === category.name)
              "
              @update:model-value="
                updateAdditionalRiskControlMitigationStrategies(category, $event)
              "
            />
            <div
              v-if="checkIfAnyElementVerticalScrollEnabled(category.name)"
              :id="`risk-category--addition-risk-control-show-more--button-${getTypeControlId(
                category.name
              )}`"
              class="show-more-link"
              @click="
                displayDetailsDialog(
                  'Additional Risk Control Mitigation Strategies',
                  category.additionalRiskControlMitigationStrategies
                )
              "
            >
              Show more
            </div>
          </v-col>
        </v-row>
      </div>
    </div>
    <div
      v-if="(!riskCategories || 0 >= riskCategories.length) && !archivedExcursionDetected"
      class="excursion-waiting"
    >
      <v-progress-circular
        :size="30"
        color="rgb(29, 175, 236)"
        class="loading-img"
        indeterminate
      ></v-progress-circular>
    </div>
    <div>
      <cs-form-dialog
        id="dlg-risk-category--generic"
        v-model="genericDialogDisplayed"
        body-id="dlg-risk-category--generic-body"
        heading="People or Providers Responsible"
        :primary-action="{
          label: 'OK',
          eventName: 'primary-click'
        }"
        @primary-click="genericDialogDisplayed = false"
        @update:model-value="genericDialogDIsplayed = false"
      >
        <template #cs-form-dialog-content>
          {{ genericDialogMessage }}
        </template>
      </cs-form-dialog>
    </div>
  </div>
</template>

<script>
import * as types from '@/store/mutationTypes';
import riskCategoryLabel from '../lib/const/riskCategoryLabel';
import excursionStatus from '../lib/const/excursionStatus';
import StbIconTooltip from './StbIconTooltip';
import HighRisk from './HighRisk';
import SelectControl from './SelectControl';
import ActivityRiskCategory from './ActivityRiskCategory';
import ProviderSelect from './ProviderSelect';
import CompleteChecklist from './CompleteChecklist';

export default {
  name: 'RiskCategory',
  components: {
    HighRisk,
    SelectControl,
    ActivityRiskCategory,
    StbIconTooltip,
    ProviderSelect,
    CompleteChecklist
  },

  props: {
    saveDraft: {
      type: Function,
      default: () => {}
    },
    riskCategories: {
      type: Array,
      default: undefined
    },
    formControlEnabled: Boolean
  },
  emits: ['changeRiskCategoryDone'],
  data() {
    return {
      riskClasses: ['Swimming', 'Boarding'],
      personResponsiblePlaceholder:
        'Enter the full name or initials of the person or people responsible for this Risk Category, or any providers not included in the Approved Provider field',
      additionalRiskControlPlaceholder: 'Enter additional risk control mitigation strategies',
      excludedRiskCategories: ['Activities', 'Outdoor Education Providers'],
      negativeProviderAnswerHint:
        'There are approved providers that have not completed all requested information.\nPlease speak with your school’s administration staff for more information.',
      checklistFetchActionCategoryName: '',
      checklistFetchActionCount: 0,
      checklistFetchActionDoneCount: 0,
      highRiskCategoryName: '',
      genericDialogDisplayed: false,
      genericDialogMessage: '',
      genericDialogTitle: '',
      verticalScrollFunctionHash: {}
    };
  },
  computed: {
    resolvedExcursionStatus() {
      return this.$store.state.common.excursionDc.status;
    },
    archivedExcursionDetected() {
      return excursionStatus.ARCHIVED === this.resolvedExcursionStatus;
    },
    typesCount() {
      return (categoryName) => {
        return this.$store.getters['common/getTypesCountFromSelectedClasses'](categoryName);
      };
    },
    activityTypesCount() {
      return this.$store.getters['common/getTypesCountForActivity'];
    },
    approvedProvidersCount() {
      return (categoryName) => {
        return this.$store.getters['common/getSelectedApprovedProvidersCount'](categoryName);
      };
    },
    excursionPersonResponsibleEnabled() {
      return this.$store.state.common.excursionPersonResponsibleEnabled;
    },
    additionalRiskControlMitigationStrategiesEnabled() {
      return this.$store.state.common.excursionDc.additionalRiskControlMitigationStrategiesEnabled;
    },
    getTypeControlId() {
      return (categoryName) => {
        return categoryName.replace(/\s*/g, '');
      };
    },
    getProvidersByCategoryCount() {
      return (categoryName) => {
        return this.$store.getters['common/getProvidersByCategory'](categoryName).length;
      };
    },
    hasNegativeProviderAnswer() {
      return (categoryName) => {
        const providers = this.$store.getters['common/getProvidersByCategory'](categoryName);
        if (providers && providers.length > 0) {
          return providers.some((provider) => !provider.answer);
        }
        return false;
      };
    },
    excursionPolicyViewActionEnabled() {
      return this.$store.state.common.excursionPolicyViewActionEnabled;
    },
    categories() {
      return this.$store.state.common.excursionDc.categories;
    },
    checklistFetchActionInProgress() {
      return this.$store.state.common.checklistFetchActionInProgress;
    },
    checkIfAnyElementVerticalScrollEnabled() {
      return (categoryName) => {
        if (this.$refs.items) {
          Object.keys(this.$refs.items).forEach((item) => {
            const element = this.$refs.items[item].$el.getElementsByTagName('textarea')[0];
            if (element && element.id) {
              const name = String(element.id).replace('personResponsible-', '');
              this.verticalScrollFunctionHash[name] =
                this.checkIfElementVerticalScrollEnabled(element);
            }
          });
          const personResponsibleDisabled =
            !this.formControlEnabled ||
            (this.approvedProvidersCount(categoryName) <= 0 &&
              categoryName === 'Outdoor Education Providers');
          return (
            this.verticalScrollFunctionHash[String(categoryName).replaceAll(' ', '')] &&
            personResponsibleDisabled
          );
        }
        return null;
      };
    }
  },
  async mounted() {
    this.highRiskCategoryName = riskCategoryLabel.HIGH_RISK_STUDENT_MEDICAL_CONDITIONS;
    const argHash = {
      stbApiAdapter: this.$app.stbApiAdapter,
      store: this.$store,
      queryParam: this.$route.query
    };
    // check archive status
    if (!this.archivedExcursionDetected) {
      await this.$store.dispatch('common/getProviders', argHash);
    }
  },
  updated() {
    this.$app.excursionCommon.setListItemId('.v-list__item__title');
  },
  methods: {
    getToggleCategoryIconId(name) {
      return `risk-category--toggle-category--icon_${name}`;
    },
    getToggleCategoryId(name) {
      return `risk-category--toggle-category-label-${name}`;
    },
    getProviderTooltipBtnId(name) {
      return `risk-category--provider-tooltip--button-${name}`;
    },
    checkIfElementVerticalScrollEnabled(element) {
      let res = !!element.scrollTop;
      if (!res) {
        // eslint-disable-next-line no-param-reassign
        element.scrollTop = 1;
        res = !!element.scrollTop;
        // eslint-disable-next-line no-param-reassign
        element.scrollTop = 0;
      }
      return res;
    },
    displayDetailsDialog(title, text) {
      this.genericDialogTitle = title;
      this.genericDialogDisplayed = true;
      if (!text) {
        this.genericDialogMessage = text;
        return;
      }
      const stringifiedText = JSON.stringify(text);
      const showedText = stringifiedText.replaceAll('\\n', '<br/>').replaceAll('"', '');
      this.genericDialogMessage = showedText;
    },
    toggleCategory(index) {
      this.$store.commit(types.COMMON_SET_RISK_DISPLAYED, {
        riskName: this.riskCategories[index].name,
        displayed: !this.riskCategories[index].displayed
      });
    },
    updatePersonResponsible(category, value) {
      this.$store.commit(types.COMMON_SET_PERSON_RESPONSIBLE, {
        name: category.name,
        personResponsible: value
      });
    },
    updateAdditionalRiskControlMitigationStrategies(category, value) {
      this.$store.commit(types.COMMON_SET_ADDITIONAL_RISK_CONTROL_MITIGATION_STRATEGIES, {
        name: category.name,
        additionalRiskControlMitigationStrategies: value
      });
    },
    getTicked(item) {
      // const localItem = item;
      // @TOREVIEW: clone the item to avoid updating the store item
      const localItem = JSON.parse(JSON.stringify(item));

      if (this.categories && this.categories.length > 0) {
        const categoryItem = this.categories.find(
          (category) => category.CompleteChecklist && category.name === localItem.name
        );
        if (categoryItem && categoryItem.CompleteChecklist) {
          // @TOREVIEW: is this step required? as it is updating the store item
          localItem.CompleteChecklist = categoryItem.CompleteChecklist;
        }
      }

      let allBox = 0;
      let allChecked = 0;

      if (
        item &&
        localItem.CompleteChecklist &&
        localItem.CompleteChecklist.length > 0 &&
        localItem.CompleteChecklist[0].checklist &&
        localItem.CompleteChecklist[0].checklist.Strategy &&
        localItem.CompleteChecklist[0].checklist.Strategy.data
      ) {
        const { data } = localItem.CompleteChecklist[0].checklist.Strategy;
        const dataKey = Object.keys(data);

        if (data[dataKey] && data[dataKey].children && data[dataKey].children.length > 0) {
          data[dataKey].children.forEach((checkData) => {
            const checklist = checkData.children;

            if (checklist && checklist.length > 0) {
              checklist.forEach((box) => {
                if (box.children && box.children.length > 0) {
                  const boxes = box.children.filter((ck) => {
                    return ck.contentType === 'checkbox';
                  });
                  if (boxes && boxes.length > 0) {
                    allBox += boxes.length;
                  }

                  const checked = box.children.filter((ck) => {
                    return ck.ticked && ck.contentType === 'checkbox';
                  });

                  if (checked && checked.length > 0) {
                    allChecked += checked.length;
                  }
                } else if (box.contentType === 'checkbox') {
                  allBox += 1;
                  if (box.ticked) {
                    allChecked += 1;
                  }
                }
              });
            }
          });
        }
      } else {
        allChecked = -1;
      }
      if (
        localItem.CompleteChecklist &&
        localItem.CompleteChecklist.length > 0 &&
        localItem.CompleteChecklist[0].checklist
      ) {
        const { title } = localItem.CompleteChecklist[0].checklist || {};
        const { tickedRiskCategoryChecklists } = this.$store.state.common;

        const typesCount = this.typesCount(title);
        const ticked = allBox === allChecked;
        const isExcludedRiskCategories = this.excludedRiskCategories.indexOf(title) > 0;

        if (
          typesCount === 0 &&
          !isExcludedRiskCategories &&
          title in tickedRiskCategoryChecklists
        ) {
          this.deleteTickedRiskCategoryChecklists(title);
        } else if (tickedRiskCategoryChecklists[title] !== ticked) {
          this.updateTickedRiskCategoryChecklists(title, ticked);
        }
      }
      return allBox === allChecked;
    },
    updateTickedRiskCategoryChecklists(checklistTitle, ticked) {
      this.$store.commit(types.COMMON_SET_RISK_CATEGORY_CHECKLISTS_TICKED, {
        title: checklistTitle,
        ticked
      });
    },
    deleteTickedRiskCategoryChecklists(checklistTitle) {
      this.$store.commit(types.COMMON_DELETE_FROM_TICKED_RISK_CATEGORY_CHECKLISTS, {
        title: checklistTitle
      });
    },
    categorySectionDisplayed(categoryName) {
      let sectionDisplayed = false;

      if (this.highRiskCategoryName === categoryName) {
        sectionDisplayed = this.$store.state.common.excursionHighRiskStudentMedicalEnabled;
      } else {
        sectionDisplayed =
          categoryName !== this.excludedRiskCategories[1] ||
          this.getProvidersByCategoryCount(categoryName) > 0;
      }

      return sectionDisplayed;
    },
    changeActivityDone() {
      this.$emit('changeRiskCategoryDone');
    },
    async changeRiskCategory(tlrcName) {
      // tlrc stands for: top level risk category
      this.checklistFetchActionCategoryName = tlrcName;
      this.$store.commit(types.COMMON_SET_CHECKLIST_FETCH_ACTION_IN_PROGRESS, true);
      this.checklistFetchActionCount += 1;

      const queryParam = this.$route.query;
      const tlrc = this.$store.getters['common/getTlrc'](tlrcName);
      const excursionCommonAdapter = new this.$app.excursionCommon.Adapter({
        store: this.$store,
        app: this.$app
      });
      const allParams = excursionCommonAdapter.getRiskCategoryParams();
      const params = allParams.filter((p) => p.indexOf(tlrcName) >= 0);

      const that = this;

      this.$emit('changeRiskCategoryDone');
      const argHash = {
        stbApiAdapter: this.$app.stbApiAdapter,
        store: this.$store,
        queryParam,
        params,
        risk: tlrc,
        resolve() {
          that.checklistFetchActionDoneCount += 1;
          if (that.checklistFetchActionCount === that.checklistFetchActionDoneCount) {
            that.$store.commit(types.COMMON_SET_CHECKLIST_FETCH_ACTION_IN_PROGRESS, false);
            that.checklistFetchActionCount = 0;
            that.checklistFetchActionDoneCount = 0;
          }
        }
      };
      await this.$store.dispatch('common/changeRiskCategoryCompleteChecklist', argHash);
    }
  }
};
</script>
<style scoped>
.providers .label-padding {
  padding: 10px 0 0px 0;
}

.category-container {
  font-size: 1em;
  height: 48px;
  line-height: 48px;
}

.category-container .icon {
  font-size: 1.5em;
  font-weight: bold;
  color: #000000;
  cursor: pointer;
}

.category-container .name {
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.category-container .quantity {
  color: #808080;
}

.sub-category-container {
  font-size: 14px;
  color: #818080;
}

.sub-category-container .name {
  font-style: italic;
  padding-left: 2px;
}

.sub-category-container .section-btn {
  font-size: 0.8em;
}

.text-field-title {
  color: #818080;
}

.provider-tooltip-msg {
  white-space: pre-wrap;
}

.show-more-link {
  cursor: pointer;
}

:deep(.select2-container--default .select2-selection--single) {
  background: #eeeeee;
}
</style>
